import type { ReactElement, ReactNode, ReactPortal } from "react";
import { useCallback, useEffect, useState } from "react";

export const useNavigationControls = <T extends HTMLElement>(
    sliderNode: T,
    itemWidth: number,
    arrayChildren: (ReactElement<any> | number | string | Iterable<ReactNode> | ReactPortal)[]
) => {
    const [isNextDisabled, setIsNextDisabled] = useState<boolean>(false);
    const [isPreviousDisabled, setIsPreviousDisabled] = useState<boolean>(true);
    const [storedActiveSlide, setStoredActiveSlide] = useState<number>(0);
    const activeSlide = Math.min(storedActiveSlide, Math.max(arrayChildren.length - 1, 0));

    // scroll into the ref of the active slide
    useEffect(() => {
        if (sliderNode) {
            sliderNode.scrollTo({
                left: activeSlide * itemWidth,
                behavior: "smooth",
            });
        }
    }, [activeSlide, itemWidth, sliderNode]);

    const scrollToSlide = useCallback(
        (nextActiveSlide: number) => {
            if (sliderNode) {
                // Scroll to slide passed as argument
                const validSlideIndex = Math.min(nextActiveSlide, arrayChildren.length - 1);
                setStoredActiveSlide(() => {
                    sliderNode.scrollTo({
                        left: validSlideIndex * itemWidth,
                        behavior: "smooth",
                    });
                    return validSlideIndex;
                });
            }
        },
        [itemWidth, sliderNode, arrayChildren.length]
    );

    const scrollToNextSlide = useCallback(() => {
        if (sliderNode) {
            // Scroll 1 slide to the right
            setStoredActiveSlide((prevActiveSlide) => {
                const nextActiveSlide = Math.min(prevActiveSlide + 1, arrayChildren.length - 1);
                sliderNode.scrollTo({
                    left: nextActiveSlide * itemWidth,
                    behavior: "smooth",
                });
                return nextActiveSlide;
            });
        }
    }, [itemWidth, sliderNode, arrayChildren.length]);

    const scrollToPrevSlide = useCallback(() => {
        if (sliderNode) {
            // Scroll 1 slide to the left
            setStoredActiveSlide((prevActiveSlide) => {
                const currentActiveSlide = Math.min(
                    prevActiveSlide,
                    Math.max(arrayChildren.length - 1, 0)
                );
                const nextActiveSlide = Math.max(currentActiveSlide - 1, 0);
                sliderNode.scrollTo({
                    left: nextActiveSlide * itemWidth,
                    behavior: "smooth",
                });
                return nextActiveSlide;
            });
        }
    }, [itemWidth, sliderNode, arrayChildren.length]);

    const scrollToFirstSlide = useCallback(() => {
        if (sliderNode) {
            setStoredActiveSlide(0);
            sliderNode.scrollTo({
                left: 0,
                behavior: "smooth",
            });
        }
    }, [sliderNode]);

    // check if the slide is visible in the viewport and set the active slide accordingly

    useEffect(() => {
        if (sliderNode) {
            const getDisabledState = () => {
                const { scrollLeft, scrollWidth, clientWidth } = sliderNode;
                // -1 is needed to account for the fact that scrollLeft is rounded to the nearest integer
                const isNextDisabled = scrollLeft + clientWidth >= scrollWidth - 1;
                const isPreviousDisabled = scrollLeft === 0;

                setIsNextDisabled(isNextDisabled);
                setIsPreviousDisabled(isPreviousDisabled);
            };

            const observer = new ResizeObserver((entries) => {
                entries.forEach(() => {
                    getDisabledState();
                });
            });

            observer.observe(sliderNode);

            sliderNode.addEventListener("scroll", getDisabledState);

            return () => {
                sliderNode.removeEventListener("scroll", getDisabledState);
                observer.disconnect();
            };
        }
        // Depend on arrayChildren.length (a primitive) instead of arrayChildren
        // (a fresh array reference every render). The previous dep made this
        // effect re-attach the ResizeObserver + scroll listener on every render,
        // which under React 19 thrashes the reconciler hard enough that click
        // events on unrelated buttons elsewhere on the page get swallowed until
        // a scroll forces a fresh commit. `.length` still catches the
        // null-then-content case the original comment described.
    }, [sliderNode, arrayChildren.length]);

    return {
        scrollToNextSlide,
        scrollToPrevSlide,
        isNextDisabled,
        scrollToFirstSlide,
        isPreviousDisabled,
        activeSlide,
        setActiveSlide: setStoredActiveSlide,
        scrollToSlide,
    };
};
